import { call, put, select, take } from 'typed-redux-saga/macro';
import { format } from 'url';

import { INTENT_PROTOCOL } from 'Lib/intent/constants';
import { getHandlerConfigFromParsedIntent } from 'Lib/intent/utils';
import type { GetActionByType } from 'Lib/reduxType';
import { withErrorHandling } from 'Lib/sagaHelpers';

import { navigate } from 'Store/routing/actions';
import { SET_ROUTE_INFO } from 'Store/routing/constants';

import { clearIntent } from './actions';
import type { HANDLE_INTENT } from './constants';
import { getIntentParsedResult } from './selectors';

export function* navigateToPage(route: string) {
  yield* put(navigate(route));
  yield* take([SET_ROUTE_INFO]); // wait until route update to prevent race condition
}

export const handleIntentRoutingSaga = withErrorHandling(
  function* _handleIntentRoutingSaga(action: GetActionByType<typeof HANDLE_INTENT>) {
    const { extraPayload = {} } = action.payload;
    const { fromUserAction } = extraPayload;

    const parsedResult = yield* select(getIntentParsedResult);
    if (!parsedResult) {
      return;
    }

    const { protocol } = parsedResult;

    // only allow from user action to prevent security issue (e.g. use tinder to redirect to external site)
    if (protocol?.startsWith('http') && fromUserAction) {
      // direct html link
      yield* call(window.open, format(parsedResult), '_blank', 'noopener,noreferrer');
      yield* put(clearIntent());
      return;
    }

    // defensive against schema
    if (protocol !== INTENT_PROTOCOL) {
      yield* put(clearIntent());
      return;
    }

    const handlerConfig = getHandlerConfigFromParsedIntent(parsedResult);
    if (!handlerConfig) {
      yield* put(clearIntent());
      return;
    }

    const { route, handler, userActionOnly } = handlerConfig;

    if (userActionOnly && !fromUserAction) {
      yield* put(clearIntent());
      return;
    }

    // simple navigation
    if (!handler && route) {
      yield* call(navigateToPage, route);
      yield* put(clearIntent());
      return;
    }

    // custom logic, dynamic load logic
    if (handler) {
      // retrieve custom logic
      const handlerFunction = (yield* call(handler))?.default;
      yield* call(handlerFunction, parsedResult, handlerConfig, extraPayload);
    }
    // always clean up intent action after handling
    yield* put(clearIntent());
  },
  { name: 'handleIntentRoutingSaga' }
);
